'use client';

// Modra-only pilot, based on core 3.0.127: move request implementations and
// analytics-only currency resolution off the initial product-card path.

import type { CartItemPackages } from '@eliadmin/core/src/api/cart/cart-types';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import type {
  AttributesEntity,
  ProductDetailData,
  VariantsEntity,
} from '@eliadmin/core/src/api/products/types/product-types';
import type { ProductDisplayData as Product } from '@eliadmin/core/src/api/products/types/product-types';
import { trackEcommerce } from '@eliadmin/core/src/components/analytics/analytics-helpers';
import { AddToCartDialog } from '@eliadmin/core/src/components/dialog/lazy-dialogs';
import { AddToCartVariantDialog } from '@eliadmin/core/src/components/dialog/lazy-dialogs';
import type { ButtonProps } from '@eliadmin/core/src/components/ui/button';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { useDialogStore } from '@eliadmin/core/src/components/ui/dialog';
import { useAddToCart } from '@eliadmin/core/src/hooks/cart/use-add-to-cart';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import type { SelectedValue } from '@eliadmin/core/src/stores/category-store';
import type { SelectedServices } from '@eliadmin/core/src/stores/variant-store';
import {
  sortVariantsByAvailability,
  VariantsProvider,
  useVariantsStore,
} from '@eliadmin/core/src/stores/variant-store';
import { normalizeProductQuantity } from '@eliadmin/core/src/utils/product-utils';
import { getProductPriceDisplay } from '@eliadmin/core/src/utils/product-price-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { FiShoppingBag } from 'react-icons/fi';
import { captureClientException } from '../../../src/performance/client-error-reporting';

type AddToCartButtonProps = ButtonProps & {
  product: ProductDetailData | Product;
  quantity?: number;
  className?: string;
  showIcon?: boolean;
  sellable?: boolean;
  customText?: string;
  type?: string;
  customValuesError?: boolean;
  skipVariantDialog?: boolean;
};

function getSelectedValuesForVariant(
  variant: VariantsEntity | undefined,
  attributes: AttributesEntity[] | null | undefined,
): SelectedValue[] {
  if (!variant?.values?.length || !attributes?.length) return [];

  return attributes.reduce<SelectedValue[]>((selectedValues, attribute) => {
    const variantValue = variant.values?.find(value => value.attribute_id === attribute.id);
    const attributeValue = attribute.values?.find(value => value.id === variantValue?.id);

    if (attributeValue) {
      selectedValues.push({
        attribute,
        value: attributeValue,
      });
    }

    return selectedValues;
  }, []);
}

export function AddToCartButton({
  className,
  showIcon,
  sellable,
  product,
  customText,
  quantity = 1,
  customValuesError = false,
  skipVariantDialog = false,
  disabled: disabledProp,
  ...props
}: AddToCartButtonProps) {
  const { t } = useTranslations();
  const locale = useLocaleStore(s => s.currentLocale);
  const openDialog = useDialogStore(s => s.open);
  const me = useCustomerStore(s => s.me);

  const addToCartMutation = useAddToCart();
  const [isAutoAdding, setIsAutoAdding] = useState(false);

  const selectedVariant = useVariantsStore(state => state.selectedVariant);
  const selectedServices = useVariantsStore(state => state.selectedServices);
  const selectedPackages = useVariantsStore(state => state.selectedPackages);
  const selectedValues = useVariantsStore(state => state.selectedValues);
  const selectedQuantity = useVariantsStore(state => state.selectedQuantity);
  const customValues = useVariantsStore(state => state.customValues);
  const selectedAccessories = useVariantsStore(state => state.selectedAccessories);
  const accessoriesQuantities = useVariantsStore(state => state.accessoriesQuantities);

  const productQuery = useQuery({
    queryKey: [CacheKey.PRODUCT_DETAIL, locale, product.id, me?.authToken ?? null],
    queryFn: () =>
      import('@eliadmin/core/src/api/slugs/slugs-api')
        .then(({ fetchSlugDetail }) =>
          fetchSlugDetail({ slug: product.slug, locale, authToken: me?.authToken }),
        )
        .then(res => res['App\\Models\\Product'])
        .catch(() => null),
    enabled: false,
  });

  const sortedSuppliers = [...(product.suppliers ?? [])].sort((a, b) => a.position - b.position);

  const currencySymbol = useGlobalStore.getState().generalLayout.currency;
  const queryClient = useQueryClient();

  // A card needs prices immediately, but analytics currency codes only after a
  // successful add. Do not mount one currency observer per SSR product card.
  const resolveCurrencyISO = () =>
    queryClient
      .ensureQueryData({
        queryKey: [CacheKey.CURRENCIES, locale],
        queryFn: () =>
          import('@eliadmin/core/src/api/general/general-api').then(({ fetchCurrencies }) =>
            fetchCurrencies({ locale }),
          ),
        staleTime: 5 * 60 * 1000,
      })
      .then(
        currencies =>
          currencies?.data.find(currency => currency.symbol === currencySymbol)?.id ||
          currencySymbol,
      )
      .catch(() => 'EUR');

  const firstSupplier = sortedSuppliers[0];
  const isProductEnded = product.status === 'ended';
  const hasUnresolvedVariants =
    (!!product.has_variants || !!product.variants?.length) && !selectedVariant;
  const priceDisplay = getProductPriceDisplay(selectedVariant ?? product);
  const priceOnRequest = !hasUnresolvedVariants && priceDisplay.priceOnRequest;

  const handleAddToCart = async () => {
    if (priceOnRequest || (!sellable && quantity < 1)) return;

    const fullProduct = product.has_variants
      ? await productQuery.refetch().then(res => res.data)
      : product;

    const desiredQuantity = normalizeProductQuantity(quantity, product);

    if (!!fullProduct && (fullProduct?.variants?.length || selectedAccessories?.length)) {
      const defaultVariant =
        selectedVariant ?? sortVariantsByAvailability(fullProduct.variants ?? [])[0];
      const defaultSelectedValues = selectedVariant
        ? selectedValues
        : getSelectedValuesForVariant(defaultVariant, fullProduct.attributes);

      openDialog({
        id: AddToCartVariantDialog.id,
        content: (
          <VariantsProvider
            state={{
              product: fullProduct,
              selectedPackages,
              selectedAccessories,
              accessoriesQuantities,
              selectedVariant: defaultVariant,
              selectedQuantity,
              selectedServices,
              selectedValues: defaultSelectedValues,
              customValues,
              customValuesError,
            }}
          >
            <AddToCartVariantDialog
              product={fullProduct}
              quantity={desiredQuantity}
              skipVariantDialog={skipVariantDialog}
              onAutoAddStateChange={setIsAutoAdding}
            />
          </VariantsProvider>
        ),
      });
    } else {
      await addToCartMutation.mutateAsync({
        locale,
        // TODO: implement quantity
        body: {
          item: {
            product: { id: product.id },
            sub_items: {
              services: selectedServices
                ? selectedServices.map((selectedService: SelectedServices) => {
                    return {
                      id: selectedService.id,
                    };
                  })
                : [],
              packages: selectedPackages
                ? selectedPackages.map(selectedPackage => {
                    return {
                      id: selectedPackage.id,
                    } as CartItemPackages;
                  })
                : [],
            },
          },
          qty: desiredQuantity,
        },
      });
      // Neither analytics lookup nor its failure may delay the cart confirmation.
      void resolveCurrencyISO()
        .then(currency =>
          trackEcommerce.addToCart({
            id: product.id.toString(),
            name: product.name,
            price: priceDisplay.currentWithVat ?? 0,
            currency,
          }),
        )
        .catch(captureClientException);
      openDialog({
        id: AddToCartDialog.id,
        content: <AddToCartDialog product={product} title="text631" />,
      });
    }
  };

  if (!firstSupplier?.is_affiliate && priceOnRequest) {
    return null;
  }

  return (
    <>
      {!firstSupplier?.is_affiliate && (
        <Button
          className={cn('space-x-1', className)}
          onClick={e => {
            e.stopPropagation();
            e.nativeEvent.preventDefault();
            handleAddToCart();
          }}
          {...props}
          disabled={
            isProductEnded || priceOnRequest || (disabledProp ?? (!sellable || customValuesError))
          }
          variant={'default'}
          isLoading={addToCartMutation.isPending || productQuery.isFetching || isAutoAdding}
        >
          {showIcon && <FiShoppingBag className="h-5 w-5" />}
          <span className={cn('line-clamp-1 inline flex-1 text-ellipsis @[200px]:hidden')}>
            {customText ? t(customText) : t('text1115')}
          </span>
          <span className={cn('line-clamp-1 hidden flex-1 text-ellipsis @[200px]:inline')}>
            {customText ? t(customText) : t('text227')}
          </span>
        </Button>
      )}
      {firstSupplier?.is_affiliate && (
        <Button
          className={cn('space-x-1', className)}
          asChild
          variant={'default'}
          isLoading={addToCartMutation.isPending || productQuery.isFetching}
          {...props}
        >
          <a href={firstSupplier.url ?? '#'}>
            {showIcon && <FiShoppingBag className="h-5 w-5" />}
            <span className={cn('line-clamp-1 inline flex-1 text-ellipsis text-center')}>
              {customText ? t(customText) : t('text1226')}
            </span>
          </a>
        </Button>
      )}
    </>
  );
}

// /**
//  * Deprecated and empty not to break other eshops
//  * @deprecated
//  */
// function addProductToAnalytics(product: ProductDetailData | Product, quantity: number = 1) {
//   //
// }
